<script setup lang="ts">
import AppGrid from "@/components/AppGrid.vue";
import { FITTABLE_DEFAULT_COL_DEF } from "@/components/composables/useGridColumnDefaults.ts";
import DashboardStateView from "@/components/dashboard/DashboardStateView.vue";
import { useRedistributionStore } from "@/stores/redistributionStore.ts";
import type { ColDef } from "ag-grid-community";
import { storeToRefs } from "pinia";
import { computed } from "vue";

const { beforeAfterGridRowData, itemMovementsGridRowData, updatedPOs } =
  storeToRefs(useRedistributionStore());

const hasData = computed(() => beforeAfterGridRowData.value.length > 0);

/* ── Summary stats ── */
const totalAssessed = computed(() => beforeAfterGridRowData.value.length);
const totalMoved = computed(() => itemMovementsGridRowData.value.length);
const criticalCount = computed(
  () =>
    beforeAfterGridRowData.value.filter((row) => (row.urgency as number) >= 2)
      .length
);
const avgWosAfter = computed(() => {
  const allSkus = updatedPOs.value.flatMap((po) => po.skus);
  if (allSkus.length === 0) return null;
  const avg =
    allSkus.reduce((sum, s) => sum + s.daysToStockout / 7, 0) / allSkus.length;
  return avg.toFixed(1);
});

/* ── Column Definitions ── */
const columnDefs: ColDef[] = [
  {
    field: "sku",
    headerName: "SKU",
    flex: 0.8,
    minWidth: 70,
    cellRenderer: (params: { value: string }) => {
      const el = document.createElement("strong");
      el.textContent = params.value;
      return el;
    },
    filter: "agTextColumnFilter",
  },
  {
    field: "description",
    headerName: "Description",
    flex: 2,
    minWidth: 120,
    filter: "agTextColumnFilter",
  },
  {
    field: "urgency",
    headerName: "Urgency",
    flex: 0.8,
    minWidth: 70,
    sort: "desc",
    filter: "agNumberColumnFilter",
    cellRenderer: (params: { value: number }) => {
      const span = document.createElement("span");
      span.className = "dashboard-status-badge";
      if (params.value >= 2) {
        span.classList.add("dashboard-status-badge--critical");
        span.textContent = "Critical";
      } else if (params.value >= 1) {
        span.classList.add("dashboard-status-badge--moderate");
        span.textContent = "Moderate";
      } else {
        span.classList.add("dashboard-status-badge--healthy");
        span.textContent = "Healthy";
      }
      return span;
    },
  },
  {
    field: "daysToStockout",
    headerName: "Days to Stockout",
    flex: 0.9,
    minWidth: 72,
    filter: "agNumberColumnFilter",
    valueFormatter: (params: { value: number }) =>
      String(Math.round(params.value)),
  },
  {
    field: "totalBefore",
    headerName: "Qty Before",
    flex: 0.7,
    minWidth: 65,
    filter: "agNumberColumnFilter",
    cellStyle: { textAlign: "right" },
  },
  {
    field: "totalAfter",
    headerName: "Qty After",
    flex: 0.7,
    minWidth: 65,
    filter: "agNumberColumnFilter",
    cellStyle: { textAlign: "right", fontWeight: "700" },
  },
  {
    headerName: "Delta",
    flex: 0.6,
    minWidth: 60,
    filter: "agNumberColumnFilter",
    valueGetter: (params: { data: Record<string, unknown> }) =>
      (params.data.totalAfter as number) - (params.data.totalBefore as number),
    cellRenderer: (params: { value: number }) => {
      if (params.value === 0) return "—";
      const span = document.createElement("span");
      span.style.fontWeight = "600";
      if (params.value > 0) {
        span.style.color = "var(--color-success, #27ae60)";
        span.textContent = `+${String(params.value)}`;
      } else {
        span.style.color = "var(--color-danger, #e74c3c)";
        span.textContent = String(params.value);
      }
      return span;
    },
    cellStyle: { textAlign: "right" },
  },
  {
    headerName: "Status",
    flex: 0.6,
    minWidth: 60,
    valueGetter: (params: { data: Record<string, unknown> }) =>
      (params.data.totalBefore as number) !==
      (params.data.totalAfter as number),
    cellRenderer: (params: { value: boolean }) => {
      const span = document.createElement("span");
      span.className = "dashboard-status-badge";
      if (params.value) {
        span.classList.add("dashboard-status-badge--balanced");
        span.textContent = "Moved";
      } else {
        span.style.opacity = "0.45";
        span.textContent = "—";
      }
      return span;
    },
  },
];

const defaultColDef: ColDef = FITTABLE_DEFAULT_COL_DEF;

function getRowHeight(): number {
  return 48;
}
</script>

<template>
  <div class="dashboard-page page-main-container gap-5">
    <!-- Page title and subtitle live in AppHeader (route meta). -->

    <!-- Empty state -->
    <DashboardStateView
      v-if="!hasData"
      tone="empty"
      title="No SKU data available"
      message="Run a redistribution to populate SKU analysis. Select POs from the PO Recommendations workflow and submit."
    />

    <template v-else>
      <!-- Summary stats bar -->
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div class="dashboard-card rounded-[1.5rem] px-5 py-4">
          <p
            class="dashboard-text-muted text-xs font-semibold tracking-[0.14em] uppercase"
          >
            Total SKUs Assessed
          </p>
          <p class="dashboard-text-primary mt-1 text-3xl font-bold">
            {{ totalAssessed }}
          </p>
        </div>
        <div class="dashboard-card rounded-[1.5rem] px-5 py-4">
          <p
            class="dashboard-text-muted text-xs font-semibold tracking-[0.14em] uppercase"
          >
            SKUs Moved
          </p>
          <p class="dashboard-text-primary mt-1 text-3xl font-bold">
            {{ totalMoved }}
          </p>
        </div>
        <div class="dashboard-card rounded-[1.5rem] px-5 py-4">
          <p
            class="dashboard-text-muted text-xs font-semibold tracking-[0.14em] uppercase"
          >
            Critical Urgency
          </p>
          <p
            class="mt-1 text-3xl font-bold"
            :class="
              criticalCount > 0 ? 'text-red-500' : 'dashboard-text-primary'
            "
          >
            {{ criticalCount }}
          </p>
        </div>
        <div class="dashboard-card rounded-[1.5rem] px-5 py-4">
          <p
            class="dashboard-text-muted text-xs font-semibold tracking-[0.14em] uppercase"
          >
            Avg WOS After
          </p>
          <p class="dashboard-text-primary mt-1 text-3xl font-bold">
            {{ avgWosAfter != null ? `${avgWosAfter}w` : "—" }}
          </p>
        </div>
      </div>

      <!-- SKU grid wrapper with proper flex container -->
      <div class="flex min-h-0 min-w-0 flex-1 flex-col">
        <div
          class="dashboard-card flex min-h-0 flex-1 flex-col rounded-[1.5rem] px-5 py-5"
        >
          <p
            class="dashboard-divider dashboard-text-muted mb-3 shrink-0 border-b pb-3 text-xs font-semibold tracking-[0.14em] uppercase"
          >
            SKU Detail — {{ totalAssessed }} rows
          </p>
          <AppGrid
            id="sku-analysis-grid"
            class="flex-1"
            :column-defs="columnDefs"
            :row-data="beforeAfterGridRowData"
            :default-col-def="defaultColDef"
            :get-row-height="getRowHeight"
            :max-rows="20"
            show-top-border
            :no-rows-overlay-component-params="{
              getTitle: () => 'No SKUs to display',
            }"
          />
        </div>
      </div>
    </template>
  </div>
</template>
